import * as monaco from 'monaco-editor';
import {
  CompletionRequest,
  CompletionResolveRequest,
  ProtocolConnection,
  ServerCapabilities,
} from 'vscode-languageserver-protocol';

import {
  OnException,
  completionContextMapping,
  completionItemMapping,
  positionMapping,
} from '../utils/convertVscodeToMonaco';

export function completionProvider(
  languageId: string,
  connection: ProtocolConnection,
  completionOptions: ServerCapabilities['completionProvider'],
  onException: OnException,
) {
  if (completionOptions === undefined) return;

  const {
    triggerCharacters,
    allCommitCharacters,
    resolveProvider,

    // labelDetailsSupport is the only key in completionItem and it says that it can send details when resolving the item.
    // we always handle details if they are there, so I don't think we need to do anything based on this setting?
    // completionItem: { labelDetailsSupport } = {},

    // workDoneProgress, // TODO: what does this do?
  } = completionOptions;
  monaco.languages.registerCompletionItemProvider(languageId, {
    triggerCharacters,
    async provideCompletionItems(model, position, context, token) {
      const result = await connection.sendRequest(
        CompletionRequest.type,
        {
          textDocument: { uri: model.uri.toString(true) },
          position: positionMapping.monacoToVscode(position),
          context: completionContextMapping.monacoToVscode(
            context,
            onException,
          ),
        },
        token,
      );

      if (token.isCancellationRequested) {
        return null;
      }

      const items = Array.isArray(result) ? result : result?.items;
      const incomplete = !Array.isArray(result) && result?.isIncomplete;
      if (!items) return null;
      const wordStart = model.getWordUntilPosition(position);
      return {
        incomplete,
        suggestions: items.map((item) => {
          return completionItemMapping.vscodeToMonaco(
            item,
            {
              startLineNumber: position.lineNumber,
              endLineNumber: position.lineNumber,
              startColumn: wordStart.startColumn,
              endColumn: wordStart.endColumn,
            },
            allCommitCharacters,
            onException,
          );
        }),
      };
    },
    resolveCompletionItem: resolveProvider
      ? async (
          item: monaco.languages.CompletionItem,
          token: monaco.CancellationToken,
        ) => {
          const resolvedItem = await connection.sendRequest(
            CompletionResolveRequest.type,
            completionItemMapping.monacoToVscode(item, onException),
            token,
          );
          return completionItemMapping.vscodeToMonaco(
            resolvedItem,
            item.range,
            allCommitCharacters,
            onException,
          );
        }
      : undefined,
  });
}
